/**
 * Principal Session History - Sanjaya Brand Kit
 * Navy (#254B70), Orange (#F0A820), Teal (#258010)
 */
import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { 
  FileText, RefreshCw, Download, Upload, Plus,
  Home, Eye, ClipboardList, UserCheck, Phone, BarChart3, Shield, Brain
} from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import ResponsiveLayout from '../components/ResponsiveLayout';

import { principalNavItems } from '../config/principalNav';

import useSessionHistory from '../hooks/useSessionHistory';
import useBulkPasteImport from '../hooks/useBulkPasteImport';

import {
  SessionSummaryCards,
  SessionFilters,
  SessionsTable,
  EditSessionModal,
  AddSessionModal,
  SessionDetailModal
} from '../components/session-history';

const PrincipalSessionHistory = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const fileInputRef = useRef(null);
  
  const [editingSession, setEditingSession] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [viewingSession, setViewingSession] = useState(null);

  const {
    loading,
    sessions,
    summary,
    filters,
    filterOptions,
    page,
    totalPages,
    total,
    exporting,
    importing,
    setFilters,
    setPage,
    loadData,
    clearFilters,
    exportSessions,
    handleFileUpload,
    saveEdit,
    deleteSession,
    addSession,
    principalName
  } = useSessionHistory(navigate, toast);

  const bulkImport = useBulkPasteImport(
    filterOptions, 
    toast, 
    () => {
      setShowAddModal(false);
      loadData();
    }
  );

  const handleDelete = async (sessionId) => {
    const reason = prompt('Please provide a reason for deletion:');
    if (reason) {
      await deleteSession(sessionId, reason);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('principal_token');
    navigate('/principal/login');
  };

  if (loading && sessions.length === 0) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="text-center">
          <div className="w-12 h-12 sm:w-16 sm:h-16 border-4 border-sanjaya-navy/20 border-t-sanjaya-navy rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-base sm:text-lg text-slate-600 font-medium">Loading sessions...</p>
        </div>
      </div>
    );
  }

  return (
    <ResponsiveLayout
      navItems={principalNavItems}
      userRole="principal"
      userName={principalName || 'Principal'}
      onLogout={handleLogout}
    >
      <div data-testid="session-history-page" className="font-body">
        {/* Header */}
        <div className="flex flex-col gap-4 mb-5 sm:mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <h1 className="text-xl sm:text-2xl md:text-3xl font-heading font-bold text-sanjaya-navy flex items-center gap-2 sm:gap-3">
                <ClipboardList className="w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8 text-sanjaya-navy" />
                Session History
              </h1>
              <p className="text-sm sm:text-base text-slate-600 mt-1">Review and manage all session records</p>
            </div>
            <Button 
              variant="outline" 
              onClick={loadData} 
              disabled={loading}
              className="self-start sm:self-auto h-10 sm:h-11 px-4 text-sm font-semibold border-sanjaya-navy/30 text-sanjaya-navy hover:bg-sanjaya-navy/5"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Refresh
            </Button>
          </div>
          
          {/* Action Buttons */}
          <div className="flex flex-wrap gap-2 sm:gap-3">
            <Button 
              variant="outline" 
              onClick={exportSessions} 
              disabled={exporting}
              className="h-10 sm:h-11 px-3 sm:px-4 text-sm font-medium border-sanjaya-navy/30 text-sanjaya-navy hover:bg-sanjaya-navy/5"
            >
              <Download className={`w-4 h-4 mr-1.5 ${exporting ? 'animate-pulse' : ''}`} /> 
              <span className="hidden sm:inline">Download </span>CSV
            </Button>
            <div className="relative">
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,.xlsx,.xls"
                onChange={handleFileUpload}
                className="hidden"
                id="csv-upload"
              />
              <Button 
                variant="outline"
                onClick={() => fileInputRef.current?.click()}
                disabled={importing}
                className="h-10 sm:h-11 px-3 sm:px-4 text-sm font-medium border-sanjaya-teal/30 text-sanjaya-teal hover:bg-sanjaya-teal/5"
              >
                <Upload className={`w-4 h-4 mr-1.5 ${importing ? 'animate-pulse' : ''}`} /> 
                {importing ? 'Processing...' : <><span className="hidden sm:inline">Upload </span>Transcripts</>}
              </Button>
            </div>
            <Button 
              onClick={() => setShowAddModal(true)} 
              className="h-10 sm:h-11 px-3 sm:px-4 text-sm font-bold bg-sanjaya-orange hover:bg-sanjaya-orange-dark text-sanjaya-navy"
            >
              <Plus className="w-4 h-4 mr-1.5" /> Add Session
            </Button>
          </div>
        </div>

        {/* Upload Instructions */}
        <Card className="mb-4 bg-sanjaya-navy/5 border-2 border-sanjaya-navy/20">
          <CardContent className="p-3 sm:p-4">
            <p className="text-xs sm:text-sm text-sanjaya-navy">
              <strong>📤 Upload Transcripts (Excel):</strong> Upload an Excel file with columns: 
              <code className="bg-sanjaya-navy/10 px-1 rounded mx-1 text-xs">child_name</code>,
              <code className="bg-sanjaya-navy/10 px-1 rounded mx-1 text-xs">observer_name</code>,
              <code className="bg-sanjaya-navy/10 px-1 rounded mx-1 text-xs">date_timespan</code>,
              <code className="bg-sanjaya-navy/10 px-1 rounded mx-1 text-xs">transcript</code>
            </p>
            <p className="text-xs sm:text-sm text-sanjaya-teal mt-2">
              ✨ <strong>Pipeline:</strong> Each transcript auto-updates: Sessions, AI Analysis, Mood, Reports, Alerts & more!
            </p>
          </CardContent>
        </Card>

        {/* Summary Cards */}
        <div className="mb-4 sm:mb-6">
          <SessionSummaryCards summary={summary} />
        </div>

        {/* Filters */}
        <div className="mb-4">
          <SessionFilters 
            filters={filters}
            setFilters={setFilters}
            filterOptions={filterOptions}
            onClear={clearFilters}
          />
        </div>

        {/* Sessions Table */}
        <SessionsTable
          sessions={sessions}
          total={total}
          page={page}
          totalPages={totalPages}
          onPageChange={setPage}
          onEdit={setEditingSession}
          onDelete={handleDelete}
          onViewDetails={(session) => setViewingSession(session)}
        />
      </div>

      {/* Session Detail Modal - Full Analysis View */}
      {viewingSession && (
        <SessionDetailModal
          session={viewingSession}
          onClose={() => setViewingSession(null)}
          onUpdate={() => {
            loadData();
            toast({ title: "Updated", description: "Session analysis updated successfully" });
          }}
        />
      )}

      {/* Edit Modal */}
      {editingSession && (
        <EditSessionModal
          session={editingSession}
          filterOptions={filterOptions}
          onSave={saveEdit}
          onClose={() => setEditingSession(null)}
        />
      )}

      {/* Add Session Modal */}
      {showAddModal && (
        <AddSessionModal
          filterOptions={filterOptions}
          onAddSingle={addSession}
          onClose={() => {
            setShowAddModal(false);
            bulkImport.resetBulkImport();
          }}
          bulkPasteData={bulkImport.bulkPasteData}
          setBulkPasteData={bulkImport.setBulkPasteData}
          parseBulkPaste={bulkImport.parseBulkPaste}
          parsedSessions={bulkImport.parsedSessions}
          parseError={bulkImport.parseError}
          bulkImporting={bulkImport.bulkImporting}
          updateParsedSession={bulkImport.updateParsedSession}
          removeParsedSession={bulkImport.removeParsedSession}
          importBulkSessions={bulkImport.importBulkSessions}
        />
      )}
    </ResponsiveLayout>
  );
};

export default PrincipalSessionHistory;
